// packages/frontend/src/features/tonight/DoTodayCard.tsx
//
// Tonight page (Plan 12): today's checkable prep tasks. Checking a task is
// optimistic through `toggleTaskDone` (editWeek.js) — no local busy/pending
// state here, since the Dexie live query that feeds `useTonight()` re-renders
// this card the moment the optimistic write lands. Mirrors the checkbox-row
// idiom of `meal-plans/TaskList.tsx` sized up to a 48px hit target.
import type { JSX } from "react";
import { toast } from "sonner";
import { Card, CardHeader, CardTitle, CardContent } from "@/ui/card.js";
import { toggleTaskDone } from "../meal-plans/editWeek.js";
import { friendlyError } from "../../shared/http.js";
import { useAuth } from "../auth/AuthProvider.js";
import type { TonightData } from "./useTonight.js";

interface DoTodayCardProps {
  data: TonightData;
}

export function DoTodayCard({ data }: DoTodayCardProps): JSX.Element | null {
  const { me } = useAuth();
  const userId = me?.user.userId ?? null;

  if (me === null || data.plan === null || data.digest === null) return null;
  const tasks = data.digest.tasksToday;
  if (tasks.length === 0) return null;

  async function handleToggle(taskId: string): Promise<void> {
    // Guard so a check can never fire without a userId to stamp `doneBy` with.
    if (userId === null) return;
    try {
      await toggleTaskDone(data.weekStart, taskId, userId);
    } catch (e) {
      toast.error(friendlyError(e));
    }
  }

  return (
    <section aria-label="Do today">
      <Card>
        <CardHeader>
          <CardTitle>Do today</CardTitle>
        </CardHeader>
        <CardContent className="px-2">
          <ul className="flex flex-col gap-1">
            {tasks.map((task) => (
              <li key={task.id}>
                <button
                  type="button"
                  aria-label={`Toggle ${task.title}`}
                  aria-pressed={task.done}
                  onClick={() => {
                    void handleToggle(task.id);
                  }}
                  className="flex min-h-12 w-full items-center gap-3 rounded-md px-2 text-left transition-colors hover:bg-zinc-800/60"
                >
                  <span
                    aria-hidden
                    className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-sm border-2 transition-colors ${
                      task.done ? "border-emerald-500 bg-emerald-500 text-white" : "border-zinc-500 bg-transparent"
                    }`}
                  >
                    {task.done && (
                      <svg viewBox="0 0 12 12" fill="none" className="h-3 w-3" aria-hidden>
                        <path
                          d="M2 6l3 3 5-5"
                          stroke="currentColor"
                          strokeWidth={2}
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    )}
                  </span>
                  <span className={task.done ? "flex-1 truncate text-zinc-500 line-through" : "flex-1 truncate text-zinc-100"}>
                    {task.title}
                  </span>
                  {task.startByTime && (
                    <span className="shrink-0 text-xs text-amber-400">by {task.startByTime}</span>
                  )}
                </button>
              </li>
            ))}
          </ul>
        </CardContent>
      </Card>
    </section>
  );
}
